// Pure filter engine for the events page sidebar explorer.
// Filters shape:
// {
//   location: 'all' | 'online' | '<city name>',
//   date: { kind: 'all'|'today'|'tomorrow'|'week'|'next-week'|'two-weeks'|'custom', start?: Date, end?: Date },
//   types: string[],            // event-type slugs, OR-ed within the group;
//                               // an event matches via any of its eventTypes tags
//   cost: 'all' | 'free' | 'paid',
//   query: string,
// }
// Groups AND together. Facet counts always derive from the unfiltered set.

import { parseFee } from './parseFee'

export const DEFAULT_FILTERS = {
  location: 'all',
  date: { kind: 'all' },
  types: [],
  cost: 'all',
  query: '',
}

export const TYPE_OPTIONS = [
  { value: 'yoga', label: 'Yoga' },
  { value: 'meditation', label: 'Meditation' },
  { value: 'breathwork', label: 'Breathwork' },
  { value: 'retreat', label: 'Retreat' },
  { value: 'sound-healing', label: 'Sound healing' },
  { value: 'music', label: 'Music' },
  { value: 'nature', label: 'Nature' },
  { value: 'certification', label: 'Certification' },
]

export function getEventCity(event) {
  if (event.city) return event.city
  if (event.space?.city) return event.space.city
  if (event.format === 'online') return 'Online'
  return null
}

export function isFreeEvent(event) {
  // parseFee is the codebase's fee authority (used for gateway payments):
  // "Free" → 0, "₹599" → 599, "Rs 500, free for members" → 500 (paid).
  const fee = (event.fee || '').trim()
  if (!fee) return true
  if (parseFee(fee) > 0) return false
  // Parses to 0: free only when the text says so ("Free", "0", "₹0").
  // Descriptive text like "Register for details" or "Pay what you can"
  // is not free — the card shows the text itself.
  return /free/i.test(fee) || /^(?:₹|rs\.?\s*)?0(?:\.0+)?\/?-?$/i.test(fee)
}

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate())
const endOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999)
const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n)

export function dateRangeFor(kind, now) {
  if (kind === 'today') return { start: startOfDay(now), end: endOfDay(now) }
  if (kind === 'tomorrow') {
    const t = addDays(now, 1)
    return { start: startOfDay(t), end: endOfDay(t) }
  }
  if (kind === 'week') {
    // Now through the end of this week's Sunday
    const daysToSunday = (7 - now.getDay()) % 7
    return { start: startOfDay(now), end: endOfDay(addDays(now, daysToSunday)) }
  }
  if (kind === 'next-week') {
    // Monday after this week's Sunday through the following Sunday
    const daysToSunday = (7 - now.getDay()) % 7
    return {
      start: startOfDay(addDays(now, daysToSunday + 1)),
      end: endOfDay(addDays(now, daysToSunday + 7)),
    }
  }
  if (kind === 'two-weeks') {
    // This week and next week together: now through next week's Sunday
    const daysToSunday = (7 - now.getDay()) % 7
    return { start: startOfDay(now), end: endOfDay(addDays(now, daysToSunday + 7)) }
  }
  return null
}

export function eventMatchesDateRange(event, range) {
  if (!range) return true
  const start = new Date(event.eventDate)
  const end = event.endDate ? new Date(event.endDate) : start
  return start <= range.end && end >= range.start
}

function rangeForFilter(dateFilter, now) {
  if (!dateFilter || dateFilter.kind === 'all') return null
  if (dateFilter.kind === 'custom') {
    if (!dateFilter.start) return null
    return {
      start: startOfDay(dateFilter.start),
      end: endOfDay(dateFilter.end || dateFilter.start),
    }
  }
  return dateRangeFor(dateFilter.kind, now)
}

function searchText(event) {
  return [
    event.name,
    event.location,
    event.space?.name,
    event.space?.city,
    event.city,
    ...(event.facilitators || []).map((f) => f.name),
    ...(event.externalFacilitators || []),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
}

export function applyEventFilters(events, filters, now = new Date()) {
  const range = rangeForFilter(filters.date, now)
  const query = (filters.query || '').trim().toLowerCase()

  return events.filter((event) => {
    if (filters.location && filters.location !== 'all') {
      const city = getEventCity(event)
      if (filters.location === 'online') {
        // Hybrid events can be attended online, so they count as Online.
        if (event.format !== 'online' && event.format !== 'hybrid') return false
      } else if (city !== filters.location) {
        return false
      }
    }

    if (!eventMatchesDateRange(event, range)) return false

    if (filters.types && filters.types.length > 0) {
      const tags = event.eventTypes || []
      if (!tags.some((t) => filters.types.includes(t))) return false
    }

    if (filters.cost === 'free' && !isFreeEvent(event)) return false
    if (filters.cost === 'paid' && isFreeEvent(event)) return false

    if (query && !searchText(event).includes(query)) return false

    return true
  })
}

export function computeLocationCounts(events) {
  let online = 0
  const cityCounts = new Map()
  for (const event of events) {
    if (event.format === 'online' || event.format === 'hybrid') online += 1
    const city = event.city || event.space?.city || null
    if (city) cityCounts.set(city, (cityCounts.get(city) || 0) + 1)
  }
  const cities = [...cityCounts.entries()]
    .map(([city, count]) => ({ city, count }))
    .sort((a, b) => b.count - a.count || a.city.localeCompare(b.city))
  return { online, cities }
}

export function computeTypeCounts(events) {
  const counts = {}
  for (const event of events) {
    for (const tag of event.eventTypes || []) {
      counts[tag] = (counts[tag] || 0) + 1
    }
  }
  return counts
}

const toISODate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

const parseISODate = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || '')
  if (!m) return null
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
}

export function filtersToSearchParams(filters) {
  const params = new URLSearchParams()
  if (filters.location && filters.location !== DEFAULT_FILTERS.location) {
    params.set('loc', filters.location)
  }
  const date = filters.date || DEFAULT_FILTERS.date
  if (date.kind === 'custom' && date.start) {
    // Empty end = range selection pending (first calendar click); preserving it
    // lets the second click complete the range after the URL round-trip.
    params.set('date', `${toISODate(date.start)}_${date.end ? toISODate(date.end) : ''}`)
  } else if (date.kind !== DEFAULT_FILTERS.date.kind) {
    params.set('date', date.kind)
  }
  if (filters.types && filters.types.length > 0) {
    params.set('types', filters.types.join(','))
  }
  if (filters.cost && filters.cost !== DEFAULT_FILTERS.cost) {
    params.set('cost', filters.cost)
  }
  if (filters.query && filters.query.trim()) {
    params.set('q', filters.query.trim())
  }
  return params
}

export function searchParamsToFilters(params) {
  const filters = { ...DEFAULT_FILTERS, date: { ...DEFAULT_FILTERS.date }, types: [] }

  const loc = params.get('loc')
  if (loc) filters.location = loc

  const date = params.get('date')
  if (
    date === 'all' ||
    date === 'today' ||
    date === 'tomorrow' ||
    date === 'week' ||
    date === 'next-week' ||
    date === 'two-weeks'
  ) {
    filters.date = { kind: date }
  } else if (date && date.includes('_')) {
    const [startStr, endStr] = date.split('_')
    const start = parseISODate(startStr)
    const end = parseISODate(endStr)
    // end stays null while a range selection is pending (start-only URLs).
    if (start) filters.date = { kind: 'custom', start, end }
  }

  const types = params.get('types')
  if (types) {
    const valid = new Set(TYPE_OPTIONS.map((t) => t.value))
    filters.types = types.split(',').filter((t) => valid.has(t))
  }

  const cost = params.get('cost')
  if (cost === 'free' || cost === 'paid') filters.cost = cost

  const q = params.get('q')
  if (q) filters.query = q

  return filters
}
